import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { listsService } from '@/services/lists.service';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Users,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

export function ListsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [activeOnly, setActiveOnly] = useState(true);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['lists', { search, page, activeOnly }],
    queryFn: () =>
      listsService.getAll({
        search,
        page,
        limit: 10,
        activeOnly,
      }),
  });

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta lista?')) {
      return;
    }

    try {
      await listsService.delete(id);
      toast({
        title: 'Lista removida',
        description: 'A lista foi removida com sucesso',
        variant: 'success',
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Erro ao remover lista',
        variant: 'destructive',
      });
    }
  };

  const getFiltersDescription = (filters: any) => {
    if (!filters) return null;

    const parts: string[] = [];
    if (filters.companyIds?.length > 0) {
      parts.push(`${filters.companyIds.length} empresa(s)`);
    }
    if (filters.positionIds?.length > 0) {
      parts.push(`${filters.positionIds.length} cargo(s)`);
    }
    if (filters.tagIds?.length > 0) {
      parts.push(`${filters.tagIds.length} tag(s)`);
    }

    return parts.length > 0 ? parts.join(', ') : null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Listas</h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie suas listas de clientes
          </p>
        </div>
        <Button onClick={() => navigate('/app/listas/nova')} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Nova Lista
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
            <Input
              placeholder="Buscar por nome ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>
          <Button
            variant={activeOnly ? 'default' : 'outline'}
            onClick={() => setActiveOnly(!activeOnly)}
          >
            <Filter className="w-4 h-4 mr-2" />
            {activeOnly ? 'Ativas' : 'Todas'}
          </Button>
        </div>
      </Card>

      {/* Lists */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          Carregando...
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {data?.data.map((list) => (
              <motion.div
                key={list.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
              >
                <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all cursor-pointer">
                  <div className="p-6" onClick={() => navigate(`/app/listas/${list.id}`)}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-serif text-brand-gold">
                            {list.name}
                          </h3>
                          {!list.active && (
                            <span className="px-2 py-1 text-xs rounded-full bg-brand-smoke/50 text-brand-ivory/50">
                              Inativa
                            </span>
                          )}
                        </div>

                        {list.description && (
                          <p className="mt-2 text-sm text-brand-ivory/60">
                            {list.description}
                          </p>
                        )}

                        <div className="mt-3 flex flex-wrap gap-4 text-sm text-brand-ivory/50">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            <span>{list.membersCount} membro(s)</span>
                          </div>
                          {getFiltersDescription(list.filters) && (
                            <div className="flex items-center gap-2">
                              <Filter className="w-4 h-4" />
                              <span>Filtros: {getFiltersDescription(list.filters)}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/app/listas/${list.id}`)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(list.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-4 text-brand-ivory">
                Página {page} de {data.totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page + 1)}
                disabled={page === data.totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {data?.data.length === 0 && (
            <div className="text-center py-12">
              <p className="text-brand-ivory/50">Nenhuma lista encontrada</p>
              <Button
                onClick={() => navigate('/app/listas/nova')}
                variant="outline"
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar primeira lista
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
